import { FC, isValidElement, useState } from 'react';
import {
  Box,
  Flex,
  VStack,
  HStack,
  IconButton,
  useDisclosure,
  Drawer,
  DrawerContent,
  Text,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  useColorModeValue,
  useColorMode,
  Tooltip,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  FormControl,
  FormLabel,
  Input,
  Tag,
  TagLabel,
  Icon,
  DrawerOverlay,
} from '@chakra-ui/react';
import { FiMenu, FiChevronDown } from 'react-icons/fi';
import { IoMdCalendar, IoMdMoon, IoMdSunny } from 'react-icons/io';
import { IoLanguage } from 'react-icons/io5';
import { MdCheckCircleOutline } from 'react-icons/md';
import { LogoIcon } from '../components';
import { useAuth, useLanguage, useDateRange, useProfile, RoleLevel, ISession, PARTNER_ROUTES } from '@bemap/utils';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { base64, handleTeamName } from '@bemap/utils';
import { CloseIcon } from '@chakra-ui/icons';
import { SidebarLink, TopbarTab } from '../types';

export interface DashboardLayoutProps {
  user: ISession | null;
  sidebarLinks: SidebarLink[];
  topbarTabs?: TopbarTab[];
  children: React.ReactNode;
  sidebarHeader?: React.ReactNode;
  topbarRight?: React.ReactNode;
  activeSubsystem?: string;
  hideTopbarTabs?: boolean;
  appVersion?: string;
}

const HeaderLinks: FC = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const { t } = useTranslation();
  const { user, signout } = useAuth();
  const firstName = user?.name?.split(' ')[0] || '';
  const navigate = useNavigate();
  const { updateLanguage } = useLanguage();
  const { profilePhotoState, profile_photo, switchProfile } = useProfile();
  const {
    isOpen: isDateModalOpen,
    onOpen: onDateModalOpen,
    onClose: onDateModalClose
  } = useDisclosure();
  const {
    selectedRange,
    tempDates,
    dateRanges,
    handleDateChange,
    handleDateRangeSelect,
    resetDates,
    saveDateChanges,
    closeModal
  } = useDateRange();

  // Chakra Color Mode
  const navbarIcon = useColorModeValue('gray.400', 'white');
  const menuBg = useColorModeValue('white', 'blackAlpha.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
  const shadow = useColorModeValue(
    '14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
    '14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
  );

  const handleSaveDateChanges = () => {
    if (saveDateChanges()) {
      onDateModalClose();
    }
  };

  const handleCloseModal = () => {
    closeModal();
    onDateModalClose();
  };

  return (
    <>
      <Flex
        w={{ sm: '100%', md: 'auto' }}
        alignItems='center'
        flexDirection='row'
        flexWrap='nowrap'
        p='10px'>
        <Menu>
          {/* <Tooltip label={t('search.placeholder')}>
            <GlobalSearch />
          </Tooltip> */}
          <Tooltip label={t('account.language')}>
            <MenuButton>
              <Icon
                mt='8px'
                me='10px'
                h='18px'
                w='18px'
                color={navbarIcon}
                as={IoLanguage}
              />
            </MenuButton>
          </Tooltip>
          <MenuList>
            <MenuItem onClick={() => updateLanguage('pt-BR')}>Português (BR)</MenuItem>
            <MenuItem onClick={() => updateLanguage('en-US')}>English (US)</MenuItem>
            <MenuItem onClick={() => updateLanguage('es-ES')}>Español (ES)</MenuItem>
          </MenuList>
        </Menu>
        <Tooltip label={t('dates.tooltip')}>
          <Button
            onClick={onDateModalOpen}
            variant='no-hover'
            bg='transparent'
            p='0px'
            minW='unset'
            minH='unset'
            h='18px'
            w='max-content'>
            <Icon
              me='10px'
              h='18px'
              w='18px'
              color={navbarIcon}
              as={IoMdCalendar}
            />
          </Button>
        </Tooltip>
        <Button
          variant='no-hover'
          bg='transparent'
          p='0px'
          minW='unset'
          minH='unset'
          h='18px'
          w='max-content'
          onClick={toggleColorMode}>
          <Icon
            me='10px'
            h='18px'
            w='18px'
            color={navbarIcon}
            as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
          />
        </Button>
        <Menu>
          <MenuButton p='0px'>
            <Avatar
              _hover={{ cursor: 'pointer' }}
              color='white'
              name={user?.name}
              src={base64(profile_photo || profilePhotoState)}
              bg='blue.500'
              size='sm'
              w='30px'
              h='30px'
            />
          </MenuButton>
          <MenuList boxShadow={shadow} p='0px' mt='10px' borderRadius='20px' bg={menuBg} border='none'>
            <Flex w='100%' mb='0px'>
              <Text
                ps='20px'
                pt='16px'
                pb='10px'
                w='100%'
                borderBottom='1px solid'
                borderColor={borderColor}
                fontSize='sm'
                fontWeight='700'
                color={textColor}>
                👋&nbsp; {t('account.greeting')} {firstName}
              </Text>
            </Flex>
            <Flex py='8px' flexDirection='column' px='24px' borderBottom='1px solid' borderColor={borderColor}>
              {user?.teams?.map(team => (
                <MenuItem
                  key={team.id}
                  _hover={{ bg: 'none' }}
                  _focus={{ bg: 'none' }}
                  px='0'
                  borderRadius='8px'
                  onClick={() => switchProfile(team.id || 0)}
                  alignItems='center'
                  gap={'5px'}>
                  {handleTeamName(team.name, team.type)} {user.currentTeamId === team.id && <MdCheckCircleOutline offset='3px' size='18px' color='#48BB78' />}
                </MenuItem>
              ))}
            </Flex>
            <Flex flexDirection='column' p='10px'>
              {user?.permission === RoleLevel.PARTNER && (
                <MenuItem _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} borderRadius='8px' px='14px' onClick={() => navigate(PARTNER_ROUTES.TEAM)}>
                  <Text fontSize='sm'>{t('account.team_settings')}</Text>
                </MenuItem>
              )}
              <MenuItem _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} borderRadius='8px' px='14px' onClick={() => navigate(PARTNER_ROUTES.PROFILE)}>
                <Text fontSize='sm'>{t('account.menu_title')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => signout()}
                _hover={{ bg: 'none' }}
                _focus={{ bg: 'none' }}
                color='red.400'
                borderRadius='8px'
                px='14px'>
                <Text fontSize='sm'>{t('actions.logout')}</Text>
              </MenuItem>
            </Flex>
          </MenuList>
        </Menu>
      </Flex>
      <Modal isOpen={isDateModalOpen} onClose={handleCloseModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('dates.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex gap={4} direction={'column'}>
              <Flex gap={2} flexWrap={'wrap'}>
                {dateRanges.map((range) => (
                  <Tag
                    key={range.value}
                    size="sm"
                    variant={selectedRange === range.value ? 'subtle' : 'outline'}
                    colorScheme="blue"
                    onClick={() => handleDateRangeSelect(range.value)}
                    cursor="pointer"
                  >
                    <TagLabel>{range.label}</TagLabel>
                  </Tag>
                ))}
              </Flex>
              <FormControl id="startDate" mt={4}>
                <FormLabel>{t('dates.start')}</FormLabel>
                <Input
                  type="date"
                  value={tempDates.startDate}
                  onChange={handleDateChange('start')}
                />
              </FormControl>
              <FormControl id="endDate" mt={4}>
                <FormLabel>{t('dates.end')}</FormLabel>
                <Input
                  type="date"
                  value={tempDates.endDate}
                  onChange={handleDateChange('end')}
                />
              </FormControl>
            </Flex>
          </ModalBody>

          <ModalFooter gap="10px">
            <Button variant={'ghost'} onClick={resetDates} colorScheme="red">
              {t('dates.reset')}
            </Button>
            <Button variant={"outline"} onClick={handleCloseModal}>
              {t('actions.cancel')}
            </Button>
            <Button colorScheme={"blue"} onClick={handleSaveDateChanges}>
              {t('actions.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const BottomNavigation: FC<{ sidebarLinks: SidebarLink[] }> = ({ sidebarLinks }) => {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const boxShadow = useColorModeValue('0 -2px 10px rgba(0, 0, 0, 0.1)', '0 -2px 10px rgba(0, 0, 0, 0.3)');
  const activeButtonBg = useColorModeValue('gray.800', 'blue.900');
  const hoverActiveButtonBg = useColorModeValue('gray.700', 'blue.900');
  const selectedActiveButtonColor = useColorModeValue('blue.50', 'blue.900');
  const unSelectedActiveButtonColor = useColorModeValue('gray.200', 'gray.600');

  const bottomNavLinks = sidebarLinks
    .filter(link => Boolean(link.section))
    .slice(0, 4);

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      bg={'gray.900'}
      borderTopWidth="1px"
      borderColor={borderColor}
      zIndex={1000}
      display={{ base: 'block', md: 'none' }}
      boxShadow={boxShadow}
    >
      <Flex justify="space-around" align="center" py={2}>
        {bottomNavLinks.map((link) => {
          const isActive = currentPath === link.href;
          return (
            <Button
              key={link.label}
              as="a"
              href={link.href}
              onClick={link.onClick}
              variant="ghost"
              size="sm"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minH="60px"
              w="full"
              maxW="80px"
              borderRadius="md"
              color={isActive ? 'blue.500' : 'gray.400'}
              bg={isActive ? activeButtonBg : 'transparent'}
              _hover={{
                bg: isActive ? hoverActiveButtonBg : 'gray.700',
                color: isActive ? 'blue.600' : 'blue.500',
              }}
              _active={{
                bg: isActive ? selectedActiveButtonColor : unSelectedActiveButtonColor,
              }}
              transition="all 0.2s"
            >
              <Box mb={1}>
                {isValidElement(link.icon) ? link.icon : null}
              </Box>
              <Text fontSize="xs" fontWeight={isActive ? 'semibold' : 'normal'}>
                {link.label}
              </Text>
            </Button>
          );
        })}
      </Flex>
    </Box>
  );
};

export const DashboardLayout: FC<DashboardLayoutProps> = ({
  user,
  sidebarLinks,
  topbarTabs,
  children,
  sidebarHeader,
  topbarRight,
  activeSubsystem,
  hideTopbarTabs = false,
  appVersion = '0.0.0'
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('dashboard-sidebar-collapsed');
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });

  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

  const handleSidebarToggle = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    if (typeof window !== 'undefined') {
      localStorage.setItem('dashboard-sidebar-collapsed', JSON.stringify(newState));
    }
  };

  const filteredSidebarLinks = sidebarLinks.filter(link => link.roles?.includes(user?.permission || 0));

  const sections = filteredSidebarLinks.reduce((acc, link) => {
    const section = link.section || 'Other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(link);
    return acc;
  }, {} as Record<string, SidebarLink[]>);

  const SidebarContent = (
    <Box
      as="nav"
      w={{ base: 'full', md: isSidebarCollapsed ? 16 : 64 }}
      pos="fixed"
      h="full"
      bg={useColorModeValue("gray.900", "gray.800")}
      borderRightWidth="1px"
      borderColor={useColorModeValue("gray.900", "gray.700")}
      zIndex={100}
      px={isSidebarCollapsed ? 2 : 4}
      py={6}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      boxShadow="sm"
      transition="width 0.2s ease-in-out"
    >
      <Box>
        <Flex align="center" justifyContent={isSidebarCollapsed ? "center" : "flex-start"} h={10} pb={4} borderBottomWidth="1px" borderColor="gray.800">
          <LogoIcon />
          <Text fontWeight="semibold" color="white" ml={2} fontSize="lg" display={{ base: 'none', md: isSidebarCollapsed ? 'none' : 'inline-block' }}>
            BeMap
          </Text>
        </Flex>
        {sidebarHeader && <Box mb={4}>{sidebarHeader}</Box>}
        <VStack align="stretch" spacing={4} mt={4}>
          {Object.entries(sections).map(([section, links]) => links.length > 0 && (
            <Box key={section}>
              {section !== 'Other' && !isSidebarCollapsed && (
                <Text px={2} mb={1} fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                  {section}
                </Text>
              )}
              <VStack align="stretch" spacing={1}>
                {links.map(link => {
                  const isActive = currentPath.startsWith(link.href);
                  return (
                    <Button
                      key={link.label}
                      onClick={() => navigate(link.href)}
                      leftIcon={isValidElement(link.icon) ? link.icon : undefined}
                      variant="ghost"
                      justifyContent={isSidebarCollapsed ? "center" : "flex-start"}
                      fontWeight={isActive ? 'bold' : 'normal'}
                      color={isActive ? 'brand.500' : 'white'}
                      bg={isActive ? 'gray.800' : 'transparent'}
                      _hover={{ color: 'brand.500', bg: 'gray.800' }}
                      borderRadius="md"
                      px={isSidebarCollapsed ? 2 : 3}
                      py={2}
                      fontSize="md"
                      transition="background 0.2s, color 0.2s"
                      title={isSidebarCollapsed ? link.label : undefined}
                    >
                      {!isSidebarCollapsed && link.label}
                    </Button>
                  );
                })}
              </VStack>
            </Box>
          ))}
        </VStack>
      </Box>

      <Box>
        <Text fontSize="xs" color="gray.500">
          {`v${appVersion ?? '0.0.0'}`}
        </Text>
      </Box>
    </Box>
  );

  // Topbar
  return (
    <Box minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')} overflowX="hidden">
      <Box display={{ base: 'none', md: 'block' }}>{SidebarContent}</Box>
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay backdropFilter="blur(4px)" bg="blackAlpha.300" />
        <DrawerContent position="relative">
          <IconButton
            aria-label="Close menu"
            icon={<CloseIcon />}
            position="absolute"
            top={4}
            zIndex={1000}
            right={4}
            onClick={onClose}
            variant="ghost"
            size="sm"
            color="white"
            _hover={{ bg: 'whiteAlpha.200' }}
          />
          {SidebarContent}
        </DrawerContent>
      </Drawer>
      {/* Topbar */}
      <Flex
        as="header"
        position={{ base: "fixed", md: "sticky" }}
        w={{ base: "100%", md: "auto" }}
        top={0}
        zIndex={99}
        h={16}
        align="center"
        bg={useColorModeValue('white', 'gray.800')}
        ml={{ base: 0, md: isSidebarCollapsed ? 16 : 64 }}
        borderBottomWidth="1px"
        borderColor={useColorModeValue('gray.200', 'gray.700')}
        px={{ base: 4, md: 8 }}
        justify="space-between"
        boxShadow="sm"
      >
        <HStack spacing={4} align="center">
          <IconButton
            aria-label="Open menu"
            icon={<FiMenu />}
            display={{ base: 'inline-flex', md: 'none' }}
            onClick={onOpen}
            variant="ghost"
            size="md"
          />
          <IconButton
            aria-label="Toggle sidebar"
            icon={<FiMenu />}
            display={{ base: 'none', md: 'inline-flex' }}
            onClick={handleSidebarToggle}
            variant="ghost"
            size="md"
          />
        </HStack>

        {/* Topbar Tabs */}
        {!hideTopbarTabs && topbarTabs && topbarTabs.length > 1 && (
          <>
            {/* Desktop Tabs */}
            <HStack spacing={2} align="center" flex={1} justify="flex-start" display={{ base: 'none', sm: 'flex' }} ml={4}>
              {topbarTabs.map((tab) => {
                const isActive = activeSubsystem === tab.id;
                return (
                  <Button
                    key={tab.label}
                    variant={isActive ? 'solid' : 'ghost'}
                    colorScheme={isActive ? 'blue' : 'gray'}
                    size="sm"
                    onClick={tab.onClick}
                    fontWeight={isActive ? 'semibold' : 'normal'}
                    px={4}
                    py={2}
                    borderRadius="md"
                    transition="all 0.2s"
                    bg={isActive ? useColorModeValue('blue.50', 'blue.900') : "transparent"}
                    color={isActive ? 'blue.500' : useColorModeValue('gray.600', 'gray.300')}
                    _hover={{
                      bg: isActive ? useColorModeValue('blue.50', 'blue.900') : useColorModeValue('gray.100', 'gray.700'),
                    }}
                    _active={{
                      bg: isActive ? 'blue.700' : 'gray.200',
                    }}
                    position="relative"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    backdropFilter="blur(8px)"
                    border={isActive ? '1px solid' : '1px solid transparent'}
                    borderColor={'transparent'}
                  >
                    {tab.label}
                  </Button>
                );
              })}
            </HStack>

            {/* Mobile Tabs Dropdown */}
            <Box display={{ base: 'block', sm: 'none' }}>
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<FiChevronDown />}
                  variant="ghost"
                  size="sm"
                  px={3}
                >
                  {topbarTabs.find(tab => activeSubsystem === tab.id)?.label || 'Menu'}
                </MenuButton>
                <MenuList>
                  {topbarTabs.map((tab) => {
                    const isActive = activeSubsystem === tab.id;
                    return (
                      <MenuItem
                        key={tab.label}
                        onClick={tab.onClick}
                        bg={isActive ? useColorModeValue('blue.50', 'blue.900') : 'transparent'}
                        color={isActive ? 'blue.600' : useColorModeValue('inherit', 'gray.300')}
                        fontWeight={isActive ? 'semibold' : 'normal'}
                      >
                        {tab.label}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </Menu>
            </Box>
          </>
        )}

        <HStack spacing={4} align="center">
          {topbarRight}
          <HeaderLinks />
        </HStack>
      </Flex>
      {/* Main content */}
      <Box
        ml={{ base: 0, md: isSidebarCollapsed ? 16 : 64 }}
        pt={{ base: 16, md: 2 }}
        px={{ base: 4, md: 8 }}
        pb={{ base: 24, md: 8 }}
        transition="margin-left 0.2s ease-in-out"
      >
        {children}
      </Box>

      {/* Bottom Navigation */}
      <BottomNavigation sidebarLinks={filteredSidebarLinks} />
    </Box>
  );
}; 